import React, { Fragment } from "react";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { Table } from "antd";
import { useNavigate } from "react-router-dom";
import { GetDecryptToken } from "../../helper/http-common";
import { formatKycCreatedOnDate } from "./kycHelpers";

const SAP_ORDER_NUMBER_KEYS = ["sapOrderNo", "ispl_SAPOrderNo", "ispl_SapOrderNo", "sapOrderNumber"];
const CUSTOMER_PO_KEYS = [
	"customerPOReference",
	"customerPoReference",
	"customerPONo",
	"customerPoNo",
	"customerPO",
	"poNumber",
	"poNo",
	"customerRefNo",
	"ispl_CustomerPOReference",
	"ispl_CustomerPONumber",
	"customer_PO_Number",
	"customerPoNumber",
];

const stripLeadingZeros = (value) => {
	const text = String(value || "").trim();
	if (!text) {
		return "";
	}
	return /^0+\d+$/.test(text) ? text.replace(/^0+/, "") || "0" : text;
};

const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}
	const o = String(tableSorter.order || "").toUpperCase();
	if (o === "ASC") {
		return "ascend";
	}
	if (o === "DESC") {
		return "descend";
	}
	return null;
};

const SERVER_SORTER = { compare: (_a, _b) => 0 };

const getCellValue = (record, keys) => {
	const keyList = Array.isArray(keys) ? keys : [keys];
	for (const key of keyList) {
		const value = record?.[key];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}
	return "-";
};

const getRawValue = (record, keys) => {
	const value = getCellValue(record, keys);
	return value === "-" ? "" : String(value);
};

const formatDateValue = (value) => {
	if (value === undefined || value === null || value === "" || value === "-") {
		return "-";
	}
	return formatKycCreatedOnDate(value);
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}

	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const resolveOrderId = (record) =>
	getRawValue(record, ["orderId", "crmOrderId", "ispl_OrderId", "id"]);

const KycOrdersTab = ({
	tabTableData,
	isLoading,
	tableSorter,
	OnhandleTableSortColumn,
	materialDetailsContext = null,
	partnerDetailsContext = null,
	emptyText = "No data found",
}) => {
	const navigate = useNavigate();
	const totalRecords = Number(tabTableData.totalRecords || 0);
	const currentPage = Number(tabTableData.currentPage || 1);
	const pageSize = Number(tabTableData.pageSize || 10);

	const publicO2wToken = GetDecryptToken() || "";

	const getPublicO2wOverviewUrl = (record) => {
		const sapOrderNumber = stripLeadingZeros(getRawValue(record, SAP_ORDER_NUMBER_KEYS));
		if (!sapOrderNumber || !publicO2wToken) {
			return "";
		}

		const overviewId = getRawValue(record, CUSTOMER_PO_KEYS) || sapOrderNumber;
		return `/public/o2w/overviewDetails/all/${encodeURIComponent(
			overviewId
		)}?token=${encodeURIComponent(publicO2wToken)}&sapOrderNumber=${encodeURIComponent(
			sapOrderNumber
		)}`;
	};

	const handleTableAscDes = (pagination, filters, sorter) => {
		const s = Array.isArray(sorter) ? sorter[0] || {} : sorter || {};
		const sortOrder = s.order;
		const rawField = s.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: tableSorter.field;

		let nextOrder = tableSorter.order;
		if (sortOrder === "ascend") {
			nextOrder = "ASC";
		} else if (sortOrder === "descend") {
			nextOrder = "DESC";
		} else {
			const pageChanged =
				pagination.current !== tabTableData.currentPage ||
				(pagination.pageSize != null &&
					tabTableData.pageSize != null &&
					pagination.pageSize !== tabTableData.pageSize);
			if (!pageChanged && String(sortField) === String(tableSorter.field)) {
				const o = String(tableSorter.order || "").toUpperCase();
				nextOrder = o === "DESC" ? "ASC" : "DESC";
			}
		}

		OnhandleTableSortColumn({
			sortColumn: sortField,
			orderBy: nextOrder,
			currentPage: pagination.current,
			pageSize: pagination.pageSize,
		});
	};

	const renderLinkValue = (record, keys, onClick) => {
		const value = getCellValue(record, keys);
		if (value === "-") {
			return <span>{value}</span>;
		}
		if (!onClick) {
			return <span className="kyc-primary-contact-link">{value}</span>;
		}
		return (
			<span
				className="kyc-primary-contact-link"
				role="button"
				tabIndex={0}
				onClick={onClick}
				onKeyDown={(event) => {
					if (event.key === "Enter" || event.key === " ") {
						onClick(event);
					}
				}}
			>
				{value}
			</span>
		);
	};

	const handleOrderDetailsClick = (event, record) => {
		if (event) {
			event.stopPropagation();
		}
		const resolvedOrderId = resolveOrderId(record);
		if (!resolvedOrderId) {
			return;
		}

		const crmOrderNumber = getRawValue(record, [
			"crmOrderNo",
			"crmOrderNumber",
			"ispl_CRMOrderNo",
			"ispl_CrmOrderNo",
			"orderNo",
		]);

		if (materialDetailsContext?.productName) {
			navigate(`/kyc/order-details/${encodeURIComponent(resolvedOrderId)}`, {
				state: {
					orderId: resolvedOrderId,
					crmOrderNumber,
					returnSource: "material",
					materialProductName: materialDetailsContext.productName,
					materialName: materialDetailsContext.materialName || materialDetailsContext.productName,
					groupCompanyId: materialDetailsContext.groupCompanyId || "",
					groupCompanyName: materialDetailsContext.groupCompanyName || "",
					materialsCustomer: materialDetailsContext.materialsCustomer || "",
					accountName: materialDetailsContext.accountName || "",
					returnTab: materialDetailsContext.returnTab || "materials",
				},
			});
			return;
		}

		if (partnerDetailsContext?.crmGuid) {
			navigate(`/kyc/order-details/${encodeURIComponent(resolvedOrderId)}`, {
				state: {
					orderId: resolvedOrderId,
					crmOrderNumber,
					returnSource: "partner",
					partnerCrmGuid: partnerDetailsContext.crmGuid,
					customer: partnerDetailsContext.customer || "",
					groupCompanyId: partnerDetailsContext.groupCompanyId || "",
					groupCompanyName: partnerDetailsContext.groupCompanyName || "",
					accountName: partnerDetailsContext.accountName || "",
					materialsCustomer: partnerDetailsContext.materialsCustomer || "",
					returnTab: partnerDetailsContext.returnTab || "account",
				},
			});
			return;
		}

		navigate(`/kyc/order-details/${encodeURIComponent(resolvedOrderId)}`, {
			state: {
				orderId: resolvedOrderId,
				crmOrderNumber,
				returnTab: "orders",
			},
		});
	};

	const tableColumns = [
		{
			title: "SAP Order Number",
			dataIndex: "sapOrderNo",
			key: "sapOrderNo",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 170,
			sortOrder: getSortOrder(tableSorter, "sapOrderNo"),
			render: (_, record) => {
				const value = getCellValue(record, SAP_ORDER_NUMBER_KEYS);
				if (value === "-") {
					return <span>{value}</span>;
				}
				const href = getPublicO2wOverviewUrl(record);
				if (!href) {
					return <span className="kyc-primary-contact-link">{value}</span>;
				}
				return (
					<a
						className="kyc-primary-contact-link"
						href={href}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(event) => event.stopPropagation()}
					>
						{value}
					</a>
				);
			},
		},
		{
			title: "CRM Order Number",
			dataIndex: "crmOrderNo",
			key: "crmOrderNo",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 170,
			sortOrder: getSortOrder(tableSorter, "crmOrderNo"),
			render: (_, record) => {
				const orderId = resolveOrderId(record);
				return renderLinkValue(
					record,
					["crmOrderNo", "crmOrderNumber", "ispl_CRMOrderNo", "ispl_CrmOrderNo", "orderNo"],
					orderId
						? (event) => handleOrderDetailsClick(event, record)
						: undefined
				);
			},
		},
		{
			title: "Order Type",
			dataIndex: "orderType",
			key: "orderType",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 150,
			sortOrder: getSortOrder(tableSorter, "orderType"),
			render: (_, record) => (
				<span>{getCellValue(record, ["orderType", "ispl_OrderType", "ispl_orderType"])}</span>
			),
		},
		{
			title: "Sold to Party",
			dataIndex: "soldToParty",
			key: "soldToParty",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "soldToParty"),
			render: (_, record) => (
				<span>{getCellValue(record, ["soldToParty", "ispl_SoldToParty", "ispl_soldToParty"])}</span>
			),
		},
		{
			title: "Ship to Party",
			dataIndex: "shipToParty",
			key: "shipToParty",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "shipToParty"),
			render: (_, record) => (
				<span>{getCellValue(record, ["shipToParty", "ispl_ShipToParty", "ispl_shipToParty"])}</span>
			),
		},
		{
			title: "Bill To Party",
			dataIndex: "billToParty",
			key: "billToParty",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "billToParty"),
			render: (_, record) => (
				<span>{getCellValue(record, ["billToParty", "ispl_BillToParty", "ispl_billToParty"])}</span>
			),
		},
		{
			title: "Payer",
			dataIndex: "payer",
			key: "payer",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "payer"),
			render: (_, record) => (
				<span>{getCellValue(record, ["payer", "ispl_Payer", "ispl_payer"])}</span>
			),
		},
		{
			title: "Currency",
			dataIndex: "currency",
			key: "currency",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 120,
			sortOrder: getSortOrder(tableSorter, "currency"),
			render: (_, record) => (
				<span>{getCellValue(record, ["currency", "ispl_Currency", "curr"])}</span>
			),
		},
		{
			title: "Sales Organization",
			dataIndex: "salesOrganization",
			key: "salesOrganization",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 190,
			sortOrder: getSortOrder(tableSorter, "salesOrganization"),
			render: (_, record) => (
				<span>
					{getCellValue(record, ["salesOrganization", "ispl_SalesOrganization", "salesOrg"])}
				</span>
			),
		},
		{
			title: "Distribution Channel",
			dataIndex: "distributionChannel",
			key: "distributionChannel",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 190,
			sortOrder: getSortOrder(tableSorter, "distributionChannel"),
			render: (_, record) => (
				<span>
					{getCellValue(record, [
						"distributionChannel",
						"ispl_DistributionChannel",
						"distributionchannel",
					])}
				</span>
			),
		},
		{
			title: "Division",
			dataIndex: "division",
			key: "division",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "division"),
			render: (_, record) => (
				<span>{getCellValue(record, ["division", "ispl_Division", "ispl_division"])}</span>
			),
		},
		{
			title: "Exchange Rate",
			dataIndex: "exchangeRate",
			key: "exchangeRate",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 150,
			sortOrder: getSortOrder(tableSorter, "exchangeRate"),
			render: (_, record) => (
				<span>{getCellValue(record, ["exchangeRate", "ispl_ExchangeRate"])}</span>
			),
		},
		{
			title: "Quotation No",
			dataIndex: "quotationNo",
			key: "quotationNo",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "quotationNo"),
			render: (_, record) => (
				<span>{getCellValue(record, ["quotationNo", "ispl_QuotationNo", "quotationNumber"])}</span>
			),
		},
		{
			title: "Customer PO Reference",
			dataIndex: "customerPOReference",
			key: "customerPOReference",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "customerPOReference"),
			render: (_, record) => (
				<span>
					{getCellValue(record, [
						"customerPOReference",
						"customerPoReference",
						"customerRefNo",
						"ispl_CustomerPOReference",
					])}
				</span>
			),
		},
		{
			title: "Customer PO Reference Date",
			dataIndex: "customerPOReferenceDate",
			key: "customerPOReferenceDate",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "customerPOReferenceDate"),
			render: (_, record) =>
				formatDateValue(
					getRawValue(record, [
						"customerPOReferenceDate",
						"customerPoReferenceDate",
						"customerRefDate",
						"ispl_CustomerPOReferenceDate",
					])
				),
		},
		{
			title: "RM's Rate (only visible to Employee)",
			dataIndex: "rmsRate",
			key: "rmsRate",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 260,
			sortOrder: getSortOrder(tableSorter, "rmsRate"),
			render: (_, record) => (
				<span>
					{getCellValue(record, [
						"rmsrate",
						"rmsRate",
						"rmRate",
						"rmsRateOnlyVisibleToEmployee",
						"ispl_RMsRate",
						"ispl_RMSRate",
					])}
				</span>
			),
		},
		{
			title: "Owner",
			dataIndex: "owner",
			key: "owner",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 170,
			sortOrder: getSortOrder(tableSorter, "owner"),
			render: (_, record) => (
				<span>{getCellValue(record, ["owner", "ownerID", "ownerId", "ownerName"])}</span>
			),
		},
		{
			title: "Action",
			key: "action",
			width: 90,
			fixed: "right",
			render: (_, record) => (
				<div
					className={`tbl_info_details ${resolveOrderId(record) ? "" : "disabled"}`}
					onClick={(event) => handleOrderDetailsClick(event, record)}
					role="presentation"
				>
					<InfoCircleTwoTone />
				</div>
			),
		},
	];

	return (
		<Fragment>
			<Table
				bordered
				scroll={{ x: 3200 }}
				className="logistic_tables kyc_logistic_tables kyc_orders_tables"
				dataSource={tabTableData.rows}
				columns={tableColumns}
				sortDirections={["ascend", "descend"]}
				onChange={handleTableAscDes}
				rowKey={(record, index) =>
					resolveOrderId(record) ||
					getRawValue(record, ["sapOrderNo", "ispl_SAPOrderNo", "crmOrderNo", "crmOrderNumber"]) ||
					index
				}
				locale={{ emptyText }}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize,
					responsive: true,
					showSizeChanger: false,
					showTotal: formatRecordCount,
				}}
				loading={isLoading}
			/>
		</Fragment>
	);
};

export default KycOrdersTab;
